import { useCallback, useLayoutEffect, useRef, useState } from 'react'

/** Height assumed for a row that has not been measured yet (a single-line row), in pixels. */
export const ESTIMATED_ROW_HEIGHT: number = 50

/** Rows rendered beyond each edge of the viewport, so a fast scroll does not show blank space. */
export const OVERSCAN_ROWS: number = 10

/**
 * Rows rendered when the viewport height is not known (before the first layout, and in jsdom).
 * Generous enough that any normal-sized list renders whole.
 */
export const FALLBACK_WINDOW_ROWS: number = 60

export type RowWindow = {
    /** Index of the first rendered row. */
    readonly start: number
    /** Index after the last rendered row. */
    readonly end: number
    /** Height of the spacer above the rendered rows, standing in for the rows before `start`. */
    readonly paddingTop: number
    /** Height of the spacer below the rendered rows, standing in for the rows from `end` on. */
    readonly paddingBottom: number
    /** Ref callback for a rendered row: records its real height so the spacers stay accurate. */
    readonly measureRow: (key: string) => (element: Nullable<HTMLElement>) => void
    /**
     * Ref callback for the element that scrolls the rows. A callback rather than a ref object, so the
     * scroll listener attaches whenever the element mounts - inside a modal it is not there on the
     * first render.
     */
    readonly scrollContainerRef: (element: Nullable<HTMLElement>) => void
}

/**
 * Windows a long list inside a scrolling container: only the rows in (and near) the viewport are
 * rendered, with spacers above and below that keep the scrollbar the size of the whole list. Row
 * heights are measured as rows render (a failed row is taller, as its error detail sits under the
 * pill); unmeasured rows use {@link ESTIMATED_ROW_HEIGHT}.
 *
 * The window only decides what is rendered. Selection, filtering and select-all work on the full
 * list in state, so rows outside the window are still selected, counted and run.
 *
 * @param keys stable keys of every row in display order.
 */
export const useWindowedRows = (keys: readonly string[]): RowWindow => {
    const [container, setContainer] = useState<OnlyNullable<HTMLElement>>(null)
    const scrollContainerRef = useCallback((element: Nullable<HTMLElement>): void => setContainer(element ?? null), [])
    const heights = useRef<Map<string, number>>(new Map())
    const [scrollTop, setScrollTop] = useState<number>(0)
    const [viewportHeight, setViewportHeight] = useState<number>(0)
    const [, setMeasureVersion] = useState<number>(0)

    useLayoutEffect((): (() => void) | void => {
        if (!container) {
            setViewportHeight(0)

            return undefined
        }

        let frame: OnlyNullable<number> = null
        const onScroll = (): void => {
            if (frame !== null) return
            frame = requestAnimationFrame((): void => {
                frame = null
                setScrollTop(container.scrollTop)
            })
        }
        const onResize = (): void => setViewportHeight(container.clientHeight)

        onResize()
        container.addEventListener('scroll', onScroll, { passive: true })

        const observer: Nullable<ResizeObserver> =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize)

        observer?.observe(container)

        return (): void => {
            container.removeEventListener('scroll', onScroll)
            observer?.disconnect()
            if (frame !== null) cancelAnimationFrame(frame)
        }
    }, [container])

    const measureRow = useCallback(
        (key: string) =>
            (element: Nullable<HTMLElement>): void => {
                if (!element) return

                const height: number = element.getBoundingClientRect().height

                if (height > 0 && heights.current.get(key) !== height) {
                    heights.current.set(key, height)
                    setMeasureVersion((version: number): number => version + 1)
                }
            },
        []
    )

    const heightOf = (key: string): number => heights.current.get(key) ?? ESTIMATED_ROW_HEIGHT

    if (viewportHeight <= 0) {
        const end: number = Math.min(keys.length, FALLBACK_WINDOW_ROWS)
        let paddingBottom: number = 0

        for (let index: number = end; index < keys.length; index += 1) paddingBottom += heightOf(keys[index])

        return { start: 0, end, paddingTop: 0, paddingBottom, measureRow, scrollContainerRef }
    }

    let start: number = 0
    let offset: number = 0

    while (start < keys.length && offset + heightOf(keys[start]) <= scrollTop) {
        offset += heightOf(keys[start])
        start += 1
    }

    let end: number = start
    let visibleHeight: number = 0

    while (end < keys.length && visibleHeight < viewportHeight) {
        visibleHeight += heightOf(keys[end])
        end += 1
    }

    const windowStart: number = Math.max(0, start - OVERSCAN_ROWS)
    const windowEnd: number = Math.min(keys.length, end + OVERSCAN_ROWS)
    let paddingTop: number = 0
    let paddingBottom: number = 0

    for (let index: number = 0; index < windowStart; index += 1) paddingTop += heightOf(keys[index])
    for (let index: number = windowEnd; index < keys.length; index += 1) paddingBottom += heightOf(keys[index])

    return { start: windowStart, end: windowEnd, paddingTop, paddingBottom, measureRow, scrollContainerRef }
}
